import {useMemo} from 'react';

import PropTypes from 'prop-types';

import {Component} from '@amblique/king-living/app/components/contentful/component';
import HeroBannerNativeVideo from '@amblique/king-living/app/components/hero-banner-background-video';
import {isRealTouchDevice} from '@amblique/king-living/app/utils/is-real-touch-device';
import {normalizeVideoId} from '@amblique/king-living/app/utils/video-utils';

const DIRECT_VIDEO_FILE_REGEX = /\.(?:mp4|webm|ogg|mov|avi|mkv|m4v)(?:\?|$)/i;

const HERO_VIDEO_OPTIONS = {
  autoplay: true,
  controls: false,
  loop: true,
  mute: true,
  muted: true,
  playsinline: true,
  readonly: true,
  autopilot: false,
  size: 'cover',
};

/** Mobile no-content: natural aspect ratio. */
export const HERO_BANNER_MOBILE_NATURAL_VIDEO_OPTIONS = {
  objectFit: 'contain',
  size: 'contain',
};

function ensureHttpsUrl(url) {
  if (!url || typeof url !== 'string') return null;

  const trimmed = url.trim();

  return trimmed.startsWith('//') ? `https:${trimmed}` : trimmed;
}

function resolveHeroVideoSrc(component) {
  const candidates = [component?.videoAsset?.url, normalizeVideoId(component?.videoIdOrUrl)];

  for (const candidate of candidates) {
    const url = ensureHttpsUrl(candidate);

    if (url && DIRECT_VIDEO_FILE_REGEX.test(url)) {
      return url;
    }
  }

  return null;
}

// Image wins. Else desktop = backgroundMedia video; mobile = backgroundVideoMobile || desktop.
export function resolveHeroBackgroundLayers({backgroundMedia, backgroundVideoMobile} = {}) {
  const backgroundImage = backgroundMedia?.__typename === 'Image' ? backgroundMedia : null;

  if (backgroundImage) {
    return {backgroundImage, desktopVideo: null, mobileVideo: null};
  }

  const desktopVideo = backgroundMedia?.__typename === 'Video' ? backgroundMedia : null;
  const mobileVideo =
    backgroundVideoMobile?.__typename === 'Video' ? backgroundVideoMobile : desktopVideo;

  return {backgroundImage: null, desktopVideo, mobileVideo};
}

/** Theme lg — sync with foundations/breakpoints.js */
export const HERO_BANNER_DESKTOP_MEDIA_QUERY = '(min-width: 992px)';

export function readIsDesktopHeroViewport() {
  if (typeof window === 'undefined' || !window.matchMedia) return undefined;

  return window.matchMedia(HERO_BANNER_DESKTOP_MEDIA_QUERY).matches;
}

export function resolveActiveHeroBackgroundVideo({desktopVideo, mobileVideo} = {}, isDesktop) {
  return (isDesktop ? desktopVideo || mobileVideo : mobileVideo || desktopVideo) || null;
}

// Image on backgroundMedia wins over video.
export function hasHeroBannerVideo({backgroundMedia, backgroundVideoMobile} = {}) {
  if (backgroundMedia?.__typename === 'Image') return false;

  return (
    backgroundMedia?.__typename === 'Video' || backgroundVideoMobile?.__typename === 'Video'
  );
}

export function hasHeroBannerBackgroundMedia({backgroundMedia, backgroundVideoMobile} = {}) {
  return (
    backgroundMedia?.__typename === 'Image' ||
    backgroundMedia?.__typename === 'Video' ||
    backgroundVideoMobile?.__typename === 'Video'
  );
}

export function withHeroBannerVideoOptions(component, videoOptions = {}) {
  const cmsOptions =
    component?.options && typeof component.options === 'object' && !Array.isArray(component.options) ?
      component.options :
      {};

  return {
    ...component,
    presets: component?.presets || 'hero',
    options: {
      ...cmsOptions,
      ...HERO_VIDEO_OPTIONS,
      ...videoOptions,
    },
  };
}

/** Native MP4 on real iOS/Android; otherwise Contentful video component. */
export const HeroBannerBackgroundVideo = ({component, videoOptions, ...mediaProps}) => {
  const directSrc = useMemo(() => resolveHeroVideoSrc(component), [component]);
  const heroComponent = useMemo(
    () => withHeroBannerVideoOptions(component, videoOptions),
    [component, videoOptions]
  );
  const useNative = Boolean(directSrc) && isRealTouchDevice();

  if (useNative) {
    return (
      <HeroBannerNativeVideo
        containerProps={mediaProps}
        objectFit={videoOptions?.objectFit === 'contain' ? 'contain' : 'cover'}
        src={directSrc}
      />
    );
  }

  return <Component component={heroComponent} {...mediaProps} />;
};

HeroBannerBackgroundVideo.displayName = 'HeroBannerBackgroundVideo';
HeroBannerBackgroundVideo.propTypes = {
  component: PropTypes.object.isRequired,
  videoOptions: PropTypes.object,
};
